import React from 'react';

import { client } from '~/api/client';
import { mailboxActionError } from '~/api/data/mailbox/errors';
import {
  type CaraWorkspaceArtifactEmailInput,
  type MailboxAccountFieldsFragment,
  OutboundEmailStatus,
  ScheduleMailboxEmailDocument,
  type ScheduleMailboxEmailMutation,
  type ScheduleMailboxEmailMutationVariables
} from '~/api/graphql';
import { mailClientName } from '~/lib/mailbox-providers';
import { cancelScheduledEmail, rescheduleScheduledEmail } from '~/lib/scheduled-send';

import type { MailboxRequestScope, MailboxStatus, SentEmail } from './use-email-composer';

const SCHEDULE_FAILED = "We weren't able to send or schedule this email. Try again in a moment.";

export const isScheduled = (send: { status: OutboundEmailStatus }) =>
  send.status === OutboundEmailStatus.Scheduled;

/**
 * A send in flight locks the draft as a delivered one does; a failure leaves it
 * open, which is what makes a failed send repairable. A scheduled email stays
 * editable until its delivery starts.
 */
export const isCommitted = (send: { status: OutboundEmailStatus }) =>
  send.status === OutboundEmailStatus.Sent || send.status === OutboundEmailStatus.Sending;

/**
 * Schedule send, Reschedule, Send now and Cancel for the composer's email.
 * The scheduled email itself is part of the composer's mailbox status, which
 * reads it with the draft and the sends; this hook owns only what acting on it
 * needs.
 */
export const useScheduledEmail = (deps: {
  artifactId: string;
  version: number;
  mailboxScope: MailboxRequestScope;
  mailboxAccounts: MailboxAccountFieldsFragment[];
  /** The composition's revision, moved by every accepted write. */
  revisionRef: React.MutableRefObject<string>;
  /** The composer's save queue; anything that sends waits behind pending saves. */
  queue: React.MutableRefObject<Promise<boolean>>;
  scheduledEmail: SentEmail | null;
  setScheduledEmail: (email: SentEmail | null) => void;
  beginMailboxMutation: () => () => void;
  refreshMailboxStatus: () => Promise<MailboxStatus | null>;
}) => {
  const {
    artifactId,
    version,
    mailboxScope,
    mailboxAccounts,
    revisionRef,
    queue,
    scheduledEmail,
    setScheduledEmail,
    beginMailboxMutation,
    refreshMailboxStatus
  } = deps;

  const [scheduling, setScheduling] = React.useState(false);
  const [scheduleError, setScheduleError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setScheduling(false);
    setScheduleError(null);
  }, [mailboxScope]);

  /**
   * Schedules the stored composition for `sendAt`, through the save queue for
   * the same reason `send` is. A refusal is in `scheduleError`; for an email
   * already sent from the mail client, the status refresh then shows it sent
   * and locked.
   */
  const scheduleSend = React.useCallback(
    (
      mailboxAccountId: string,
      email: CaraWorkspaceArtifactEmailInput,
      sendAt: Date
    ): Promise<boolean> => {
      if (!mailboxScope.active) return Promise.resolve(false);
      const finishMutation = beginMailboxMutation();
      setScheduling(true);
      setScheduleError(null);
      const showError = (error: unknown) => {
        const provider = mailboxAccounts.find(
          (account) => account.id === mailboxAccountId
        )?.provider;
        setScheduleError(
          mailboxActionError(error, SCHEDULE_FAILED, mailClientName(provider)).message
        );
      };
      const schedule = async (): Promise<boolean> => {
        let refused = false;
        try {
          const result = await client.mutate<
            ScheduleMailboxEmailMutation,
            ScheduleMailboxEmailMutationVariables
          >({
            mutation: ScheduleMailboxEmailDocument,
            variables: {
              input: {
                sendAt,
                mailboxAccountId,
                artifactId,
                artifactVersion: version,
                contentRevision: revisionRef.current,
                email
              }
            }
          });
          if (!mailboxScope.active) return false;
          const scheduled = result.data?.scheduleMailboxEmail;
          if (!scheduled) {
            refused = true;
            showError({ graphQLErrors: result.errors ?? [] });
            return false;
          }
          revisionRef.current = scheduled.contentRevision;
          setScheduledEmail(scheduled.email);
          return true;
        } catch (err) {
          if (!mailboxScope.active) return false;
          refused = true;
          showError(err);
          return false;
        } finally {
          finishMutation();
          if (mailboxScope.active) {
            setScheduling(false);
            // The refusal may mean the email moved on: already scheduled, sent
            // from the mail client, or back to a draft after a failed start.
            if (refused) await refreshMailboxStatus();
          }
        }
      };
      const next = queue.current.then(schedule);
      queue.current = next;
      return next;
    },
    [
      artifactId,
      version,
      mailboxAccounts,
      mailboxScope,
      revisionRef,
      queue,
      setScheduledEmail,
      beginMailboxMutation,
      refreshMailboxStatus
    ]
  );

  /**
   * Moves the scheduled email to `sendAt`, or sends it now when null, after
   * any save still in the queue. Resolves the server's refusal rather than
   * throwing, so each caller says it where its own dialog or button is.
   */
  const moveScheduledEmail = React.useCallback(
    (sendAt: Date | null): Promise<string | null> => {
      if (!scheduledEmail || !mailboxScope.active) return Promise.resolve(null);
      const { id } = scheduledEmail;
      const finishMutation = beginMailboxMutation();
      setScheduling(true);
      const move = async (): Promise<string | null> => {
        const { email: moved, refusal } = await rescheduleScheduledEmail(client, id, sendAt);
        finishMutation();
        if (!mailboxScope.active) return null;
        setScheduling(false);
        if (moved && isScheduled(moved)) {
          setScheduledEmail(moved);
        } else {
          // Claimed by Send now, refused, or already failed: the status read
          // says what the composer should show.
          await refreshMailboxStatus();
        }
        return refusal;
      };
      const next = queue.current.then(move);
      queue.current = next.then((refusal) => !refusal);
      return next;
    },
    [
      scheduledEmail,
      mailboxScope,
      queue,
      setScheduledEmail,
      beginMailboxMutation,
      refreshMailboxStatus
    ]
  );

  const rescheduleSend = React.useCallback(
    async (sendAt: Date): Promise<boolean> => {
      setScheduleError(null);
      const refusal = await moveScheduledEmail(sendAt);
      if (refusal) setScheduleError(refusal);
      return !refusal;
    },
    [moveScheduledEmail]
  );

  const sendScheduledEmailNow = React.useCallback(async (): Promise<boolean> => {
    setScheduleError(null);
    const refusal = await moveScheduledEmail(null);
    if (refusal) setScheduleError(refusal);
    return !refusal;
  }, [moveScheduledEmail]);

  /**
   * Cancels the scheduled email. It becomes a normal draft; its linked mailbox
   * draft, if any, stays as it is.
   */
  const cancelSchedule = React.useCallback(async (): Promise<boolean> => {
    if (!scheduledEmail || !mailboxScope.active) return false;
    const finishMutation = beginMailboxMutation();
    setScheduling(true);
    setScheduleError(null);
    try {
      const { refusal } = await cancelScheduledEmail(client, scheduledEmail.id);
      if (!mailboxScope.active) return false;
      if (refusal) {
        setScheduleError(refusal);
        return false;
      }
      setScheduledEmail(null);
      return true;
    } finally {
      finishMutation();
      if (mailboxScope.active) {
        setScheduling(false);
        // Not awaited: the server has answered, and its confirm dialog closes
        // on that answer rather than after the composer re-reads its status.
        void refreshMailboxStatus();
      }
    }
  }, [scheduledEmail, mailboxScope, setScheduledEmail, beginMailboxMutation, refreshMailboxStatus]);

  return {
    scheduleSend,
    rescheduleSend,
    sendScheduledEmailNow,
    cancelSchedule,
    scheduling,
    scheduleError,
    clearScheduleError: React.useCallback(() => setScheduleError(null), [])
  };
};
